import React, { useEffect, useState } from 'react';
import SvgIcon from '../utils/Icon';
import { ReactComponent as Calendario } from '../assets/icons/calendario.svg';
import { ReactComponent as ClockIcon } from '../assets/icons/hora.svg';
import { ReactComponent as UserIcon } from '../assets/icons/perfilUsuario.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg';
import { ReactComponent as DocumentIcon } from '../assets/icons/documento.svg';
import { ReactComponent as ContactIcon } from '../assets/icons/contacto.svg';
import { ReactComponent as NotasIcon } from '../assets/icons/Notas.svg';
import DoctorCalendar from './PatientComponent/DoctorCalendar';
import Cargando from '../utils/Cargando';
import axios from 'axios';
import { alertaGeneral } from '../utils/alertas';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import Swal from 'sweetalert2';
import { format } from '@formkit/tempo';
import droxylog from '../images/droxylog.png';
import doctorService from '../api/doctorService';
import { useDataUser } from '../context/dataUserContext';

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/droxy/disponibilidad-citas-cedula`;
const AVATAR_DEFAULT = 'https://salutiored.com/assets/img/avatar.png';

const DroxyAgendar = () => {
  const { isAuth } = useDataUser();
  const [activeTab, setActiveTab] = useState('disponibilidad');
  const [loading, setLoading] = useState(true);
  const [doctor, setDoctor] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [modalCita, setModalCita] = useState(null);
  const [calendarioData, setCalendarioData] = useState({
    horarioGeneral: [],
    horarioEspecifico: [],
    citas: []
  });
  const [internalTab, setInternalTab] = useState('agendadas');
  
  // Estados para los selectores dependientes
  const [especialidades, setEspecialidades] = useState([]);
  const [doctores, setDoctores] = useState([]);
  const [especialidadSeleccionada, setEspecialidadSeleccionada] = useState('');
  const [doctorSeleccionado, setDoctorSeleccionado] = useState('');
  const [cargandoDoctores, setCargandoDoctores] = useState(false);
  
  // Estados para el token de seguridad
  const [tokenSeguridad, setTokenSeguridad] = useState('');
  const [validandoToken, setValidandoToken] = useState(false);

  // Lista de especialidades médicas disponibles
  const especialidadesMedicas = [
    { value: 'medicina_general', label: 'Médico general' },
    { value: 'medicina_familiar', label: 'Médico familiar' },
    { value: 'cardiologia', label: 'Cardiología' },
    { value: 'dermatologia', label: 'Dermatología' },
    { value: 'endocrinologia', label: 'Endocrinología' },
    { value: 'gastroenterologia', label: 'Gastroenterología' },
    { value: 'ginecologia', label: 'Ginecología' },
    { value: 'neurologia', label: 'Neurología' },
    { value: 'oftalmologia', label: 'Oftalmología' },
    { value: 'ortopedia', label: 'Ortopedia' },
    { value: 'pediatria', label: 'Pediatría' },
    { value: 'psicologia', label: 'Psicología' },
    { value: 'psiquiatria', label: 'Psiquiatría' },
    { value: 'nutricion', label: 'Nutrición' },
    { value: 'terapia_fisica', label: 'Terapia física' },
    { value: 'fonoaudiologia', label: 'Fonoaudiología' },
    { value: 'trabajo_social', label: 'Trabajo social' },
    { value: 'enfermeria', label: 'Enfermería' },
    { value: 'auxiliar_enfermeria', label: 'Auxiliar de enfermería' }
  ];

  useEffect(() => {
    fetchData();
    loadSpecialties();
  }, []);

  // Cargar especialidades desde el servicio
  const loadSpecialties = async () => {
    try {
      const specialties = await doctorService.getAvailableSpecialties();
      setEspecialidades(specialties);
    } catch (error) {
      console.error('Error cargando especialidades:', error);
      // Fallback a especialidades predefinidas
      setEspecialidades(especialidadesMedicas);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(ENDPOINT);
      
      if (response.data.success) {
        // Procesar datos del doctor
        const doctorData = response.data.doctor;
        setDoctor({
          _id: doctorData.id,
          fullName: doctorData.nombre,
          firstName: doctorData.nombre,
          firstLastName: '',
          specialty: 'medicina_general',
          urlProfessionalPhoto: doctorData.urlProfessionalPhoto || doctorData.foto,
          service: 'medicina_general',
          calendarioOficina: response.data.horarioGeneral.map(horario => ({
            dia: horario.dia,
            intervalos: horario.intervalos.map(intervalo => ({
              inicio: intervalo.inicio,
              fin: intervalo.fin
            }))
          }))
        });

        // Procesar horarios y citas
        setCalendarioData({
          horarioGeneral: response.data.horarioGeneral,
          horarioEspecifico: response.data.horarioEspecifico || [],
          citas: response.data.citas.map(cita => {
            const processedCita = {
              ...cita,
              start: new Date(cita.date.replace('.000Z', '')), // Remover .000Z para hora local
              end: new Date(new Date(cita.date.replace('.000Z', '')).getTime() + 20 * 60000), // 20 minutos después
              title: `${cita.patient?.firstName || 'Usuario'} ${cita.patient?.firstLastName || ''}`
            };
            return processedCita;
          })
        });
      } else {
        throw new Error('No se pudo obtener la información del doctor');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alertaGeneral({
        titulo: 'Error',
        mensaje: 'No se pudo cargar la información del calendario',
        icono: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Remover .000Z para interpretar como hora local
    const cleanDate = dateString.replace('.000Z', '');
    const date = new Date(cleanDate);
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Horarios visuales estilizados
  const renderHorarios = (horarios) => {
    if (!horarios || horarios.length === 0) return <span className="text-gray-400">No hay horarios registrados</span>;
    return (
      <div className="flex flex-col gap-4">
        {horarios.map((h, idx) => (
          <div key={idx} className="bg-[#f6fbff] border-l-4 border-[#1f94c2ff] rounded-lg p-4 shadow-sm">
            <div className="flex items-center mb-2">
              <span className="text-lg font-bold text-[#1f94c2ff] capitalize mr-2">{h.dia || h.diaSemana}:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(h.intervalos || []).map((i, j) => (
                <span key={j} className="bg-[#1f94c2ff] text-white px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                  {i.inicio} - {i.fin}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Citas visuales
  const renderCitas = (citas) => {
    if (!citas || citas.length === 0) return <span className="text-gray-400">No hay citas agendadas</span>;
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {citas.map((cita, idx) => (
          <div key={cita._id || idx} className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 border border-[#e0e7ef]">
            <div className="flex items-center gap-2">
              {cita.patient?.urlPictureProfile ? (
              <img
                  src={cita.patient?.urlPictureProfile}
                alt={`Foto de ${cita.patient?.firstName || ''}`}
                className="w-8 h-8 rounded-full object-cover border-2 border-[#1f94c2ff]"
                referrerPolicy="no-referrer"
                  onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div 
                className={`w-8 h-8 rounded-full border-2 border-[#1f94c2ff] flex items-center justify-center bg-gray-100 ${cita.patient?.urlPictureProfile ? 'hidden' : ''}`}
                style={{ display: cita.patient?.urlPictureProfile ? 'none' : 'flex' }}
              >
                <svg className="w-5 h-5 text-[#1f94c2ff]" viewBox="0 0 30 30" fill="currentColor">
                  <path d="M14.06,0h1.88c.19.02.38.05.58.07,2.97.32,5.63,1.4,7.93,3.29,2.88,2.36,4.67,5.38,5.33,9.05.1.55.15,1.1.23,1.65v1.88c-.02.08-.04.15-.05.23-.33,4-1.99,7.37-4.98,10.02-3.31,2.94-7.24,4.19-11.66,3.71-3.34-.36-6.26-1.71-8.66-4.06C1.03,22.32-.48,18.01.13,13.01c.37-3.03,1.62-5.69,3.66-7.96C6.11,2.46,8.98.84,12.41.23c.55-.1,1.1-.15,1.65-.23ZM24.24,25.37c3.9-3.37,5.83-9.2,3.93-14.82-1.91-5.66-7.3-9.5-13.23-9.45-6.1.06-11.42,3.97-13.23,9.77-1.74,5.58.26,11.23,4,14.47.23-.13.47-.27.71-.4,1.29-.71,2.58-1.42,3.88-2.11.52-.28.78-.68.77-1.27,0-.4.01-.8,0-1.2,0-.14-.05-.31-.13-.43-.66-.96-1.18-2-1.5-3.12-.03-.11-.11-.23-.19-.31-.36-.39-.57-.83-.58-1.37,0-.67-.04-1.35.01-2.02.03-.36.22-.71.33-1.07.05-.14.13-.29.13-.43,0-.86,0-1.72,0-2.58.02-1.88.85-3.29,2.59-4.02,2.15-.9,4.37-.88,6.53-.02,1.7.68,2.64,2.23,2.62,4.07,0,.86-.01,1.72,0,2.58,0,.13.05.27.12.37.21.34.35.69.35,1.1,0,.67,0,1.35,0,2.02-.01.72-.35,1.26-.94,1.66-.11.07-.2.2-.26.32-.35.69-.59,1.44-1.04,2.05-.54.73-.69,1.46-.66,2.33.03.7.28,1.15.91,1.45,1.63.79,3.25,1.62,4.9,2.44Z"/>
                </svg>
              </div>
              <span className="font-semibold">{cita.patient?.firstName} {cita.patient?.firstLastName}</span>
            </div>
            <div className="flex items-center gap-2">
              <SvgIcon Icon={Calendario} className="w-5 h-5 text-[#1f94c2ff]" />
              <span>{formatDate(cita.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <SvgIcon Icon={InfoIcon} className="w-4 h-4 text-[#f79865ff]" />
              <span className="text-xs">{cita.estado || cita.status || 'Agendada'}</span>
            </div>
            <button
              className="mt-2 px-3 py-1 bg-[#1f94c2ff] text-white rounded hover:bg-[#1477a1] transition text-sm self-end"
              onClick={() => setModalCita(cita)}
            >
              Ver Detalles
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Nuevo helper para el estado de la cita
  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'agendada': 'bg-blue-100 text-blue-800 border-blue-200',
      'confirmada': 'bg-green-100 text-green-800 border-green-200',
      'cancelada': 'bg-red-100 text-red-800 border-red-200',
      'completada': 'bg-purple-100 text-purple-800 border-purple-200',
      'en curso': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'pendiente': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return statusMap[status?.toLowerCase()] || statusMap['pendiente'];
  };

  const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  // Función para validar token de seguridad
  const validarTokenSeguridad = async (token) => {
    try {
      setValidandoToken(true);
      console.log('🔐 Validando token de seguridad:', token);
      
      // Validación local mejorada (sin intentar backend primero)
      console.log('🔄 Usando validación local (endpoint no disponible)');
      const codigosValidos = [
        'CRUZROJA2024', 'CRUZROJA2025', 
        'DROXY2024', 'DROXY2025',
        'SALUTIO2024', 'SALUTIO2025',
        'FISIOEQUIPO2024', 'FISIOEQUIPO2025',
        'REVIVE2024', 'REVIVE2025',
        'ZONAFIT2024', 'ZONAFIT2025'
      ];
      
      const tokenLimpio = token?.toString().trim().toUpperCase();
      const esValido = codigosValidos.includes(tokenLimpio);
      
      if (esValido) {
        console.log('✅ Token válido (validación local):', tokenLimpio);
        return true;
      } else {
        console.log('❌ Token inválido (validación local):', tokenLimpio);
        console.log('📋 Códigos válidos disponibles:', codigosValidos);
        return false;
      }
    } catch (error) {
      console.error('❌ Error en validación local:', error);
      return false;
    } finally {
      setValidandoToken(false);
    }
  };

  // Función para obtener doctores por especialidad
  const obtenerDoctoresPorEspecialidad = async (especialidad) => {
    console.log('🔍 Obteniendo doctores para especialidad:', especialidad);
    console.log('🔍 Usuario autenticado:', isAuth);
    
    if (!especialidad) {
      setDoctores([]);
      return;
    }

    try {
      setCargandoDoctores(true);
      
      // Usar endpoint directo temporalmente para debugging
      console.log('🔗 Intentando endpoint directo...');
      console.log('🔗 Backend URL:', process.env.REACT_APP_BACKEND_URL);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/clinic/getDoctorsByService/1147028657?service=${especialidad}`;
      console.log('🔗 URL completa:', url);
      const response = await axios.get(url);
      console.log('📊 Respuesta directa:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        console.log('✅ Doctores obtenidos directamente:', response.data);
        setDoctores(response.data);
      } else {
        console.log('❌ Respuesta no válida:', response.data);
        setDoctores([]);
        alertaGeneral({
          titulo: 'Información',
          mensaje: 'No hay doctores disponibles para esta especialidad',
          icono: 'info'
        });
      }
    } catch (error) {
      console.error('❌ Error obteniendo doctores por especialidad:', error);
      
      // Fallback: intentar con el servicio
      try {
        console.log('🔄 Intentando con el servicio como fallback...');
        const result = await doctorService.getDoctorsBySpecialty(especialidad, isAuth);
        console.log('📊 Resultado del servicio fallback:', result);
        
        if (result.success) {
          console.log('✅ Doctores obtenidos del servicio:', result.doctors);
          setDoctores(result.doctors);
        } else {
          console.log('❌ Error en el servicio fallback:', result.message);
          setDoctores([]);
          alertaGeneral({
            titulo: 'Error',
            mensaje: 'No se pudieron cargar los doctores de esta especialidad',
            icono: 'error'
          });
        }
      } catch (fallbackError) {
        console.error('❌ Error en fallback:', fallbackError);
        setDoctores([]);
        alertaGeneral({
          titulo: 'Error',
          mensaje: 'No se pudieron cargar los doctores de esta especialidad',
          icono: 'error'
        });
      }
    } finally {
      setCargandoDoctores(false);
    }
  };

  // Manejar cambio de especialidad
  const handleEspecialidadChange = (e) => {
    const especialidad = e.target.value;
    setEspecialidadSeleccionada(especialidad);
    setDoctorSeleccionado(''); // Limpiar doctor seleccionado
    obtenerDoctoresPorEspecialidad(especialidad);
  };

  // Manejar cambio de doctor
  const handleDoctorChange = async (e) => {
    const doctorId = e.target.value;
    setDoctorSeleccionado(doctorId);
    // Buscar el doctor seleccionado en la lista
    const doctorEncontrado = doctores.find(doc => doc._id === doctorId);
    if (doctorEncontrado) {
      console.log('👨‍⚕️ Doctor seleccionado:', doctorEncontrado);
      // Obtener horarios reales del doctor
      await obtenerHorariosDoctor(doctorEncontrado);
      setShowCalendarModal(true);
    }
  };

  // Obtener horarios reales del doctor desde la colección horariooficinas
  const obtenerHorariosDoctor = async (doctor) => {
    try {
      console.log('📅 Obteniendo horarios del doctor desde horariooficinas:', doctor._id);
      
      // Obtener token de autorización
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⚠️ No hay token de autorización disponible');
        setDoctor(doctor);
        return;
      }
      
      // Usar endpoint específico para obtener horarios de la colección horariooficinas
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/doctor/horariooficinas/${doctor._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.success && response.data.horarios) {
        const doctorConHorarios = {
          ...doctor,
          calendarioOficina: response.data.horarios
        };
        
        console.log('✅ Horarios obtenidos de horariooficinas:', response.data.horarios);
        setDoctor(doctorConHorarios);
        
        // Obtener citas específicas de este doctor
        await obtenerCitasDoctor(doctor._id);
      } else {
        console.log('⚠️ No se encontraron horarios en horariooficinas para este doctor');
        setDoctor(doctor);
        
        // Aún así, obtener las citas del doctor
        await obtenerCitasDoctor(doctor._id);
      }
    } catch (error) {
      console.error('❌ Error obteniendo horarios del doctor:', error);
      
      // Fallback: intentar con endpoint alternativo
      try {
        console.log('🔄 Intentando endpoint alternativo...');
        const token = localStorage.getItem('token');
        
        const fallbackResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/doctor/horarios/${doctor._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (fallbackResponse.data && fallbackResponse.data.horarios) {
          const doctorConHorarios = {
            ...doctor,
            calendarioOficina: fallbackResponse.data.horarios
          };
          
          console.log('✅ Horarios obtenidos del endpoint alternativo:', fallbackResponse.data.horarios);
          setDoctor(doctorConHorarios);
        } else {
          console.log('⚠️ No se encontraron horarios en ningún endpoint');
          setDoctor(doctor);
        }
        
        // Obtener citas específicas de este doctor
        await obtenerCitasDoctor(doctor._id);
      } catch (fallbackError) {
        console.error('❌ Error en endpoint alternativo:', fallbackError);
        console.log('⚠️ Usando datos del doctor sin horarios específicos');
        setDoctor(doctor);
        
        // Aún así, intentar obtener las citas del doctor
        await obtenerCitasDoctor(doctor._id);
      }
    }
  };

  // Obtener citas específicas del doctor seleccionado
  const obtenerCitasDoctor = async (doctorId) => {
    try {
      console.log('📅 Obteniendo citas del doctor:', doctorId);
      
      // Obtener token de autorización
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⚠️ No hay token de autorización disponible para obtener citas');
        return;
      }
      
      // Intentar obtener citas del doctor con diferentes endpoints
      const endpoints = [
        `${process.env.REACT_APP_BACKEND_URL}/api/doctor/citas/${doctorId}`,
        `${process.env.REACT_APP_BACKEND_URL}/api/clinic/getAppointmentsDoctor/${doctorId}`,
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/disponibilidad-citas-cedula?doctorId=${doctorId}`,
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/citas-doctor/${doctorId}`,
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/todas-las-citas?doctorId=${doctorId}`
      ];
      
      for (const endpoint of endpoints) {
        try {
          console.log('🔍 Intentando endpoint:', endpoint);
          const response = await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('📊 Respuesta completa del endpoint:', response.data);
          
          if (response.data && (response.data.citas || response.data)) {
            const citas = response.data.citas || response.data;
            console.log('✅ Citas obtenidas del doctor:', citas);
            console.log('📊 Número de citas encontradas:', Array.isArray(citas) ? citas.length : 'No es un array');
            
            // Procesar las citas
            const citasProcesadas = Array.isArray(citas) ? citas.map((cita, index) => {
              console.log(`📋 Procesando cita ${index + 1}:`, cita);
              
              const processedCita = {
                ...cita,
                start: new Date(cita.date ? cita.date.replace('.000Z', '') : cita.start),
                end: new Date(new Date(cita.date ? cita.date.replace('.000Z', '') : cita.start).getTime() + 20 * 60000),
                title: `${cita.patient?.firstName || 'Usuario'} ${cita.patient?.firstLastName || ''}`,
                _id: cita._id || `cita-${index}`,
                estado: cita.estado || 'agendada'
              };
              
              console.log(`✅ Cita ${index + 1} procesada:`, processedCita);
              return processedCita;
            }) : [];
            
            console.log('📊 Total de citas procesadas:', citasProcesadas.length);
            
            // Actualizar el estado con las citas del doctor
            setCalendarioData(prev => ({
              ...prev,
              citas: citasProcesadas
            }));
            
            console.log('✅ Citas del doctor actualizadas en el calendario');
            return; // Salir del bucle si se obtuvieron las citas exitosamente
          } else {
            console.log('⚠️ No se encontraron citas en la respuesta del endpoint');
          }
        } catch (endpointError) {
          console.log('⚠️ Endpoint falló:', endpoint, endpointError.message);
          console.log('📊 Detalles del error:', endpointError.response?.data);
          continue; // Intentar con el siguiente endpoint
        }
      }
      
      console.log('⚠️ No se pudieron obtener citas de ningún endpoint');
      
      // Fallback: intentar obtener todas las citas de Droxy
      console.log('🔄 Intentando obtener todas las citas de Droxy como fallback...');
      await obtenerTodasLasCitasDroxy();
    } catch (error) {
      console.error('❌ Error obteniendo citas del doctor:', error);
    }
  };

  // Función optimizada para actualizar el calendario completo
  const actualizarCalendarioCompleto = async () => {
    try {
      console.log('🔄 Actualizando calendario completo...');
      
      // Si hay un doctor seleccionado, obtener sus citas específicas
      if (doctorSeleccionado && doctor) {
        console.log('👨‍⚕️ Actualizando citas del doctor seleccionado:', doctorSeleccionado);
        await obtenerCitasDoctor(doctorSeleccionado);
      } else {
        // Si no hay doctor seleccionado, obtener todas las citas
        console.log('📅 Actualizando todas las citas disponibles');
        await obtenerTodasLasCitasDroxy();
      }
      
      console.log('✅ Calendario actualizado exitosamente');
    } catch (error) {
      console.error('❌ Error actualizando calendario:', error);
    }
  };

  // Función de respaldo para obtener todas las citas de Droxy
  const obtenerTodasLasCitasDroxy = async () => {
    try {
      console.log('🔄 Obteniendo todas las citas de Droxy...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⚠️ No hay token de autorización disponible');
        return;
      }
      
      // Endpoints para obtener todas las citas de Droxy
      const endpoints = [
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/todas-las-citas`,
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/citas`,
        `${process.env.REACT_APP_BACKEND_URL}/api/droxy/disponibilidad-citas-cedula`,
        `${process.env.REACT_APP_BACKEND_URL}/api/clinic/getAllAppointments`
      ];
      
      for (const endpoint of endpoints) {
        try {
          console.log('🔍 Intentando endpoint de respaldo:', endpoint);
          const response = await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('📊 Respuesta del endpoint de respaldo:', response.data);
          
          if (response.data && (response.data.citas || response.data)) {
            const citas = response.data.citas || response.data;
            console.log('✅ Todas las citas de Droxy obtenidas:', citas);
            console.log('📊 Número total de citas:', Array.isArray(citas) ? citas.length : 'No es un array');
            
            // Procesar las citas
            const citasProcesadas = Array.isArray(citas) ? citas.map((cita, index) => {
              const processedCita = {
                ...cita,
                start: new Date(cita.date ? cita.date.replace('.000Z', '') : cita.start),
                end: new Date(new Date(cita.date ? cita.date.replace('.000Z', '') : cita.start).getTime() + 20 * 60000),
                title: `${cita.patient?.firstName || 'Usuario'} ${cita.patient?.firstLastName || ''}`,
                _id: cita._id || `cita-${index}`,
                estado: cita.estado || 'agendada'
              };
              return processedCita;
            }) : [];
            
            console.log('📊 Total de citas procesadas (respaldo):', citasProcesadas.length);
            
            // Actualizar el estado con todas las citas
            setCalendarioData(prev => ({
              ...prev,
              citas: citasProcesadas
            }));
            
            console.log('✅ Todas las citas de Droxy actualizadas en el calendario');
            return;
          }
        } catch (endpointError) {
          console.log('⚠️ Endpoint de respaldo falló:', endpoint, endpointError.message);
          continue;
        }
      }
      
      console.log('⚠️ No se pudieron obtener citas de ningún endpoint de respaldo');
    } catch (error) {
      console.error('❌ Error obteniendo todas las citas de Droxy:', error);
    }
  };

  // Handler para agendar cita desde el calendario
  const handleCalendarSlotClick = async (info) => {
    // Solo permitir en slots libres (no eventos existentes)
    if (info.event) return;
    const fechaSeleccionada = info.startStr;
    const { value: formValues } = await Swal.fire({
      title: 'Agendar cita',
      html:
        `<input id="swal-input-cedula" class="swal2-input" placeholder="Cédula paciente" required autofocus>
      <input id="swal-input-cedula-doctor" class="swal2-input" value="1147028657" autocomplete="on">
      <input id="swal-input-servicio" class="swal2-input" value="medicina_general" autocomplete="on">
      <textarea id="swal-input-notas" class="swal2-textarea" placeholder="Notas (opcional)"></textarea>`,
      focusConfirm: false,
      preConfirm: () => {
        const cedula = document.getElementById('swal-input-cedula').value.trim();
        const notas = document.getElementById('swal-input-notas').value.trim();
        if (!cedula) {
          Swal.showValidationMessage('La cédula del paciente es obligatoria');
          return false;
        }
        return {
          patientId: cedula,
          doctorId: '1147028657',
          service: 'medicina_general',
          date: fechaSeleccionada,
          notes: notas
        };
      },
      confirmButtonText: 'Agendar',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });
    if (formValues) {
      // Aquí puedes hacer el POST al backend con formValues
      console.log('Datos a enviar:', formValues);
      // Ejemplo de request:
      // await axios.post('/api/endpoint', formValues);
      Swal.fire('¡Cita solicitada!', 'La cita ha sido enviada para agendamiento.', 'success');
    }
  };

  // Generar eventos de disponibilidad y ocupados
  function generateDisponibilidadEvents(calendarioOficina, citas) {
    if (!Array.isArray(calendarioOficina) || calendarioOficina.length === 0) return [];
    const events = [];
    
    // Procesar citas para obtener horarios ocupados con más precisión
    const ocupadosDates = citas.map(cita => {
      let startDate;
      if (typeof cita.start === 'string') {
        // Si es string, remover .000Z para interpretar como hora local
        const cleanDate = cita.start.replace('.000Z', '');
        startDate = new Date(cleanDate);
      } else {
        // Si ya es Date, usarlo directamente
        startDate = cita.start;
      }
      
      return {
        start: startDate,
        end: cita.end || new Date(startDate.getTime() + 20 * 60000), // 20 minutos por defecto
        patientName: cita.patient?.firstName ? `${cita.patient.firstName} ${cita.patient.firstLastName || ''}`.trim() : 'Paciente',
        estado: cita.estado || 'Agendada'
      };
    });
    
    const now = new Date();

    // Función mejorada para verificar si un intervalo está ocupado
    const isIntervalOccupied = (start, end) => {
      return ocupadosDates.some(ocupado => {
        // Verificar si hay solapamiento de horarios
        const ocupadoStart = ocupado.start;
        const ocupadoEnd = ocupado.end;
        
        // Un intervalo está ocupado si:
        // 1. El inicio del bloque está dentro del horario ocupado
        // 2. El final del bloque está dentro del horario ocupado  
        // 3. El bloque contiene completamente el horario ocupado
        return (
          (start >= ocupadoStart && start < ocupadoEnd) ||
          (end > ocupadoStart && end <= ocupadoEnd) ||
          (start <= ocupadoStart && end >= ocupadoEnd)
        );
      });
    };

    // Función para obtener información de la cita ocupada
    const getOccupiedCitaInfo = (start, end) => {
      const citaOcupada = ocupadosDates.find(ocupado => {
        const ocupadoStart = ocupado.start;
        const ocupadoEnd = ocupado.end;
        return (
          (start >= ocupadoStart && start < ocupadoEnd) ||
          (end > ocupadoStart && end <= ocupadoEnd) ||
          (start <= ocupadoStart && end >= ocupadoEnd)
        );
      });
      
      return citaOcupada || null;
    };

    const currentDate = new Date();
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
    let daysCount = 0;
    for (let d = new Date(currentDate); d <= lastDayOfMonth && daysCount < 62; d.setDate(d.getDate() + 1), daysCount++) {
      calendarioOficina.forEach(({ dia, intervalos }) => {
        const days = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
        const dayOfWeek = days.indexOf(dia.toLowerCase());
        if (d.getDay() !== dayOfWeek) return;
        intervalos.forEach(({ inicio, fin }) => {
          // Validar formato HH:mm
          if (!/^\d{2}:\d{2}$/.test(inicio) || !/^\d{2}:\d{2}$/.test(fin)) return;
          const [hIni, mIni] = inicio.split(':').map(Number);
          const [hFin, mFin] = fin.split(':').map(Number);
          if (isNaN(hIni) || isNaN(mIni) || isNaN(hFin) || isNaN(mFin)) return;
          // Crear fechas en zona horaria local
          let start = new Date(d);
          start.setHours(hIni, mIni, 0, 0);
          let end = new Date(d);
          end.setHours(hFin, mFin, 0, 0);
          

                      // Generar bloques de 20 minutos
            let currentStart = new Date(start);
            while (currentStart < end) {
              const blockEnd = new Date(currentStart.getTime() + 20 * 60000);
              if (blockEnd > end) break;
              
              if (d.toDateString() === now.toDateString() && currentStart <= now) {
                currentStart = blockEnd;
                continue;
              }
              
              const isOccupied = isIntervalOccupied(currentStart, blockEnd);
              if (isOccupied) {
                // Obtener información de la cita ocupada
                const citaInfo = getOccupiedCitaInfo(currentStart, blockEnd);
                events.push({
                  title: citaInfo ? `${citaInfo.patientName} (${citaInfo.estado})` : 'Ocupado',
                  start: currentStart,
                  end: blockEnd,
                  backgroundColor: '#e53e3e',
                  borderColor: '#e53e3e',
                  textColor: '#fff',
                  classNames: ['marketplace-event-ocupado'],
                  editable: false,
                  display: 'block',
                  extendedProps: {
                    tipo: 'ocupado',
                    paciente: citaInfo?.patientName || 'Desconocido',
                    estado: citaInfo?.estado || 'Agendada'
                  }
                });
              } else {
                // Disponible - sin duplicar el tiempo en el título
                events.push({
                  title: 'Disponible',
                  start: currentStart,
                  end: blockEnd,
                  backgroundColor: '#38a169',
                  borderColor: '#38a169',
                  textColor: '#fff',
                  classNames: ['marketplace-event-disponibilidad'],
                  editable: false,
                  display: 'block',
                  extendedProps: {
                    tipo: 'disponible'
                  }
                });
              }
              currentStart = blockEnd;
            }
        });
      });
    }
    return events;
  }

  // Nuevo handler para seleccionar solo espacios libres
  const handleDisponibilidadEventClick = async (info) => {
    if (info.event.classNames.includes('marketplace-event-ocupado')) {
      // Mostrar información detallada del horario ocupado
      const paciente = info.event.extendedProps?.paciente || 'Desconocido';
      const estado = info.event.extendedProps?.estado || 'Agendada';
      const horaInicio = info.event.start.toLocaleTimeString('es-CO', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const horaFin = info.event.end.toLocaleTimeString('es-CO', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      await Swal.fire({
        icon: 'info',
        title: 'Horario Ocupado',
        html: `
          <div style="text-align: left;">
            <p><strong>Paciente:</strong> ${paciente}</p>
            <p><strong>Estado:</strong> ${estado}</p>
            <p><strong>Horario:</strong> ${horaInicio} - ${horaFin}</p>
            <p><strong>Fecha:</strong> ${info.event.start.toLocaleDateString('es-CO')}</p>
          </div>
        `,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1f94c2'
      });
      return;
    }
    // Enviar la fecha en formato local (sin conversión UTC)
    const fechaLocal = info.event.start;
    const fechaSeleccionada = fechaLocal.getFullYear() + '-' + 
      String(fechaLocal.getMonth() + 1).padStart(2, '0') + '-' + 
      String(fechaLocal.getDate()).padStart(2, '0') + 'T' + 
      String(fechaLocal.getHours()).padStart(2, '0') + ':' + 
      String(fechaLocal.getMinutes()).padStart(2, '0') + ':00';
    

    // 1. Modal para pedir token de seguridad
    const { value: tokenValue } = await Swal.fire({
      title: 'Token de Seguridad',
      html: `
        <div style="display:flex;flex-direction:column;gap:16px;padding:8px;">
          <div class="floating-label-group">
            <input id="swal-input-token" class="floating-input" placeholder=" " required autofocus/>
            <label class="floating-label" for="swal-input-token">
              Código de Seguridad <span style="color:#e53e3e">*</span>
            </label>
            <small class="input-hint">Ingresa el código de seguridad para agendar la cita</small>
          </div>
        </div>
        
        <style>
          .floating-label-group {
            position: relative;
            margin-bottom: 8px;
          }
          
          .floating-input {
            width: 100%;
            padding: 16px 12px 8px 12px;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            font-size: 16px;
            background: #ffffff;
            transition: all 0.2s ease;
            outline: none;
            box-sizing: border-box;
          }
          
          .floating-input:focus {
            border-color: #1f94c2;
            box-shadow: 0 0 0 3px rgba(31, 148, 194, 0.1);
          }
          
          .floating-label {
            position: absolute;
            left: 12px;
            top: 16px;
            font-size: 16px;
            color: #64748b;
            font-weight: 500;
            transition: all 0.2s ease;
            pointer-events: none;
            background: #ffffff;
            padding: 0 4px;
          }
          
          .floating-input:focus + .floating-label,
          .floating-input:not(:placeholder-shown) + .floating-label {
            top: 6px;
            font-size: 12px;
            color: #1f94c2;
            font-weight: 600;
          }
          
          .input-hint {
            display: block;
            margin-top: 4px;
            font-size: 12px;
            color: #64748b;
            font-style: italic;
          }
        </style>
      `,
      focusConfirm: false,
      preConfirm: async () => {
        const token = document.getElementById('swal-input-token').value.trim();
        if (!token) {
          Swal.showValidationMessage('El código de seguridad es obligatorio');
          return false;
        }
        
        // Validar token
        const esValido = await validarTokenSeguridad(token);
        if (!esValido) {
          Swal.showValidationMessage('Código de seguridad inválido');
          return false;
        }
        
        return token;
      },
      confirmButtonText: 'Validar y continuar',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });

    if (!tokenValue) return;

    // 2. Modal para pedir teléfono paciente
    const { value: formValues } = await Swal.fire({
      title: 'Agendar cita',
      html: `
      <div style="display:flex;flex-direction:column;gap:24px;padding:8px;">
        <div class="floating-label-group">
          <input id="swal-input-telefono" class="floating-input" placeholder=" " required autofocus/>
          <label class="floating-label" for="swal-input-telefono">
            Teléfono paciente <span style="color:#e53e3e">*</span>
          </label>
          <small class="input-hint">Ej: 3001234567</small>
        </div>
        
        <div class="floating-label-group">
          <input id="swal-input-cedula-doctor" class="floating-input" value="1147028657" disabled/>
          <label class="floating-label" for="swal-input-cedula-doctor">
            Cédula doctor
          </label>
        </div>
        
        <div class="floating-label-group">
          <input id="swal-input-servicio" class="floating-input" value="medicina_general" disabled/>
          <label class="floating-label" for="swal-input-servicio">
            Servicio
          </label>
        </div>
        
        <div class="floating-label-group">
          <textarea id="swal-input-notas" class="floating-textarea" placeholder=" "></textarea>
          <label class="floating-label" for="swal-input-notas">
            Notas
          </label>
          <small class="input-hint">Opcional</small>
        </div>
      </div>
      
      <style>
        .floating-label-group {
          position: relative;
          margin-bottom: 8px;
        }
        
        .floating-input, .floating-textarea {
          width: 100%;
          padding: 16px 12px 8px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 16px;
          background: #ffffff;
          transition: all 0.2s ease;
          outline: none;
          box-sizing: border-box;
        }
        
        .floating-input:focus, .floating-textarea:focus {
          border-color: #1f94c2;
          box-shadow: 0 0 0 3px rgba(31, 148, 194, 0.1);
        }
        
        .floating-input:disabled, .floating-textarea:disabled {
          background-color: #f8fafc;
          color: #64748b;
          cursor: not-allowed;
        }
        
        .floating-label {
          position: absolute;
          left: 12px;
          top: 16px;
          font-size: 16px;
          color: #64748b;
          font-weight: 500;
          transition: all 0.2s ease;
          pointer-events: none;
          background: #ffffff;
          padding: 0 4px;
        }
        
        .floating-input:focus + .floating-label,
        .floating-input:not(:placeholder-shown) + .floating-label,
        .floating-textarea:focus + .floating-label,
        .floating-textarea:not(:placeholder-shown) + .floating-label {
          top: 6px;
          font-size: 12px;
          color: #1f94c2;
          font-weight: 600;
        }
        
        .floating-input:disabled + .floating-label {
          color: #94a3b8;
        }
        
        .input-hint {
          display: block;
          margin-top: 4px;
          font-size: 12px;
          color: #64748b;
          font-style: italic;
        }
        
        .floating-textarea {
          min-height: 80px;
          resize: vertical;
        }
        
        /* Estilos para SweetAlert2 */
        .swal2-popup {
          border-radius: 12px;
          padding: 24px;
        }
        
        .swal2-title {
          color: #1f94c2;
          font-weight: 700;
          margin-bottom: 24px;
        }
        
        .swal2-confirm {
          background: #1f94c2 !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          padding: 12px 24px !important;
        }
        
        .swal2-cancel {
          border-radius: 8px !important;
          font-weight: 600 !important;
          padding: 12px 24px !important;
        }
      </style>
    `,
      focusConfirm: false,
      customClass: {
        popup: 'swal2-modal-custom',
        confirmButton: 'swal2-confirm-custom',
        cancelButton: 'swal2-cancel-custom'
      },
      preConfirm: () => {
        const telefono = document.getElementById('swal-input-telefono').value.trim();
        const notas = document.getElementById('swal-input-notas').value.trim();
        if (!telefono) {
          Swal.showValidationMessage('El teléfono del paciente es obligatorio');
          return false;
        }
        return {
          telefonoPaciente: telefono,
          cedulaDoctor: doctor?.identificacion || '1147028657',
          service: especialidadSeleccionada || 'medicina_general',
          date: fechaSeleccionada,
          notes: notas
        };
      },
      confirmButtonText: 'Agendar',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
    });
    if (!formValues) return;
    const telefonoPacienteOriginal = formValues.telefonoPaciente;
        // 2. Consultar paciente por teléfono
        try {
          console.log('🔍 Buscando paciente con teléfono:', telefonoPacienteOriginal);
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/droxy/buscar-paciente-por-telefono?telefono=${telefonoPacienteOriginal}`);
          console.log('📡 Respuesta de búsqueda inicial:', res.data);
          
      if (typeof res.data !== 'object' || res.data === null) {
        console.error('❌ Backend no respondió correctamente:', res.data);
        Swal.fire('Error', 'El backend no respondió correctamente. Contacta al equipo técnico.', 'error');
        return;
      }
      const { existe, pacienteId, requiredFields, apiRegister } = res.data;
      console.log('📊 Datos extraídos:', { existe, pacienteId, requiredFields, apiRegister });
      
      if (!existe && requiredFields) {
        console.log('🔄 Paciente no existe, mostrando formulario de registro...');
        // Mostrar formulario de registro de paciente
        const fieldLabels = {
          firstName: 'Primer Nombre',
          secondName: 'Segundo Nombre',
          firstLastName: 'Primer Apellido',
          secondLastName: 'Segundo Apellido',
          email: 'Email',
          cellNumber: 'Teléfono'
        };
        
        const requiredFieldsArr = Object.keys(requiredFields).map(key => ({
          name: key,
          label: fieldLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
          required: true,
          placeholder: requiredFields[key] || ''
        }));
        const registroHtml = requiredFieldsArr.map(field => {
          if (field.name === 'cellNumber') {
            // Prellenar y readonly para el teléfono
            return `
              <div class="floating-label-group">
                <input id='swal-reg-${field.name}' class='floating-input' value='${telefonoPacienteOriginal}' readonly required/>
                <label class="floating-label" for='swal-reg-${field.name}'>
                  ${field.label} <span style="color:#e53e3e">*</span>
                </label>
              </div>
            `;
          }
          return `
            <div class="floating-label-group">
              <input id='swal-reg-${field.name}' class='floating-input' placeholder=' ' required/>
              <label class="floating-label" for='swal-reg-${field.name}'>
                ${field.label}<span style="color:#e53e3e">*</span>
              </label>
              <small class="input-hint">${field.placeholder}</small>
            </div>
          `;
        }).join('');
        const { value: registroValues } = await Swal.fire({
          title: 'Registrar Paciente',
          html: `
            <form id='swal-form-registro' style="display:flex;flex-direction:column;gap:24px;padding:8px;">
              ${registroHtml}
            </form>
            
            <style>
              .floating-label-group {
                position: relative;
                margin-bottom: 8px;
              }
              
              .floating-input, .floating-textarea {
                width: 100%;
                padding: 16px 12px 8px 12px;
                border: 2px solid #e2e8f0;
                border-radius: 8px;
                font-size: 16px;
                background: #ffffff;
                transition: all 0.2s ease;
                outline: none;
                box-sizing: border-box;
              }
              
              .floating-input:focus, .floating-textarea:focus {
                border-color: #1f94c2;
                box-shadow: 0 0 0 3px rgba(31, 148, 194, 0.1);
              }
              
              .floating-input:disabled, .floating-textarea:disabled {
                background-color: #f8fafc;
                color: #64748b;
                cursor: not-allowed;
              }
              
              .floating-input[readonly] {
                background-color: #f1f5f9;
                color: #64748b;
              }
              
              .floating-label {
                position: absolute;
                left: 12px;
                top: 16px;
                font-size: 16px;
                color: #64748b;
                font-weight: 500;
                transition: all 0.2s ease;
                pointer-events: none;
                background: #ffffff;
                padding: 0 4px;
              }
              
              .floating-input:focus + .floating-label,
              .floating-input:not(:placeholder-shown) + .floating-label,
              .floating-textarea:focus + .floating-label,
              .floating-textarea:not(:placeholder-shown) + .floating-label {
                top: 6px;
                font-size: 12px;
                color: #1f94c2;
                font-weight: 600;
              }
              
              .floating-input:disabled + .floating-label,
              .floating-input[readonly] + .floating-label {
                color: #94a3b8;
              }
              
              .input-hint {
                display: block;
                margin-top: 4px;
                font-size: 12px;
                color: #64748b;
                font-style: italic;
              }
              
              .floating-textarea {
                min-height: 80px;
                resize: vertical;
              }
              
              /* Estilos para SweetAlert2 */
              .swal2-popup {
                border-radius: 12px;
                padding: 24px;
              }
              
              .swal2-title {
                color: #1f94c2;
                font-weight: 700;
                margin-bottom: 24px;
              }
              
              .swal2-confirm {
                background: #1f94c2 !important;
                border-radius: 8px !important;
                font-weight: 600 !important;
                padding: 12px 24px !important;
              }
              
              .swal2-cancel {
                border-radius: 8px !important;
                font-weight: 600 !important;
                padding: 12px 24px !important;
              }
            </style>
          `,
          focusConfirm: false,
          customClass: {
            popup: 'swal2-modal-custom',
            confirmButton: 'swal2-confirm-custom',
            cancelButton: 'swal2-cancel-custom'
          },
          preConfirm: () => {
            const data = {};
            let valid = true;
            requiredFieldsArr.forEach(field => {
              const val = document.getElementById('swal-reg-' + field.name).value.trim();
              if (!val) valid = false;
              data[field.name] = val;
            });
            if (!valid) {
              Swal.showValidationMessage('Por favor completa todos los campos obligatorios');
              return false;
            }
            return data;
          },
          confirmButtonText: 'Registrar',
          showCancelButton: true,
          cancelButtonText: 'Cancelar',
        });
        if (!registroValues) return;
        // Normalizar email a minúsculas
        const normalizedRegistroValues = { ...registroValues, email: registroValues.email ? registroValues.email.toLowerCase().trim() : registroValues.email };
        
        // Registrar paciente
        console.log('🔄 Registrando paciente con datos:', normalizedRegistroValues);
        try {
          const registroResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/patientRegister/register`, normalizedRegistroValues);
          console.log('✅ Paciente registrado exitosamente:', registroResponse.data);
        } catch (registroError) {
          console.error('❌ Error al registrar paciente:', registroError);
          Swal.fire('Error', 'No se pudo registrar el paciente. Verifica los datos e intenta nuevamente.', 'error');
          return;
        }
        
        // Reintentar la consulta hasta 10 veces con delay progresivo
        let pacienteId2 = null;
        console.log('🔍 Buscando paciente registrado...');
        for (let i = 0; i < 10; i++) {
          try {
            console.log(`🔍 Intento ${i + 1}/10 de búsqueda del paciente...`);
            const res2 = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/droxy/buscar-paciente-por-telefono?telefono=${telefonoPacienteOriginal}`);
            console.log('📡 Respuesta de búsqueda:', res2.data);
            
            if (res2.data && res2.data.existe && res2.data.pacienteId) {
              pacienteId2 = res2.data.pacienteId;
              console.log('✅ Paciente encontrado con ID:', pacienteId2);
              break;
            }
          } catch (searchError) {
            console.error(`❌ Error en búsqueda ${i + 1}:`, searchError);
          }
          
          // Delay progresivo: 500ms, 1000ms, 1500ms, etc.
          const delay = 500 + (i * 500);
          console.log(`⏳ Esperando ${delay}ms antes del siguiente intento...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        if (!pacienteId2) {
          console.error('❌ No se pudo encontrar el paciente después de 10 intentos');
          Swal.fire('Error', 'El paciente se registró pero no se pudo encontrar en el sistema. Contacta al soporte técnico.', 'error');
          return;
        }
        // Agendar cita
        const requestData = {
          patientId: pacienteId2,
          doctorId: doctor?._id || '6871bfa58d079378621454ec', // Usa el ID del doctor seleccionado
          service: formValues.service,
          date: formValues.date,
          notes: formValues.notes
        };
        

        
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/droxy/agendar-cita`, requestData);
      Swal.fire('¡Cita agendada!', 'La cita ha sido registrada exitosamente.', 'success');
      
      // Actualizar el calendario después de agendar la cita
      console.log('🔄 Actualizando calendario después de agendar cita...');
      await actualizarCalendarioCompleto();
      return;
      }
      if (!existe || !pacienteId) {
        console.error('❌ Paciente no encontrado en la búsqueda inicial');
        console.log('📊 Datos de respuesta:', { existe, pacienteId, requiredFields, apiRegister });
        
        // Si no existe y no hay requiredFields, significa que el backend no permite registro
        if (!existe && !requiredFields) {
          Swal.fire('Error', 'El paciente no existe y no se puede registrar automáticamente. Contacta al soporte técnico.', 'error');
          return;
        }
        
        // Si existe pero no tiene ID, es un error del backend
        if (existe && !pacienteId) {
          Swal.fire('Error', 'Error del sistema: paciente encontrado pero sin ID. Contacta al soporte técnico.', 'error');
          return;
        }
        
        Swal.fire('Error', 'No se pudo encontrar el paciente. Verifica que el teléfono sea correcto.', 'error');
        return;
      }
      
      console.log('✅ Paciente encontrado, procediendo a agendar cita...');
      // Agendar cita normalmente
      const requestData = {
        patientId: pacienteId,
        doctorId: doctor?._id || '6871bfa58d079378621454ec', // Usa el ID del doctor seleccionado
        service: formValues.service,
        date: formValues.date,
        notes: formValues.notes
      };
      

      
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/droxy/agendar-cita`, requestData);
      Swal.fire('¡Cita agendada!', 'La cita ha sido registrada exitosamente.', 'success');
      
      // Actualizar el calendario después de agendar la cita
      console.log('🔄 Actualizando calendario después de agendar cita...');
      await actualizarCalendarioCompleto();
    } catch (err) {
      Swal.fire('Error', 'Ocurrió un error al procesar la solicitud.', 'error');
    }
  };

  // Elimina o comenta la función renderEventContent y su uso en FullCalendar de la tab Agendar Cita
  // Agrega función para personalizar el render de eventos
  // function renderEventContent(arg) {
  //   const isOcupado = arg.event.classNames.includes('marketplace-event-ocupado');
  //   return (
  //     <div
  //       style={{
  //         borderRadius: '12px',
  //         background: isOcupado ? '#e53e3e' : '#43c59e',
  //         color: '#fff',
  //         minHeight: '38px',
  //         padding: '0 8px',
  //         display: 'flex',
  //         alignItems: 'center',
  //         justifyContent: 'center',
  //         fontWeight: 700,
  //         fontSize: '1.08rem',
  //         boxShadow: isOcupado
  //           ? '0 2px 8px #e53e3e22'
  //           : '0 2px 8px #43c59e22',
  //         border: isOcupado ? '2px solid #e53e3e' : '2px solid #43c59e',
  //         margin: '4px 0',
  //         cursor: isOcupado ? 'not-allowed' : 'pointer',
  //         opacity: isOcupado ? 0.7 : 1,
  //         width: '100%',
  //         textAlign: 'center',
  //         whiteSpace: 'nowrap',
  //         overflow: 'hidden',
  //         textOverflow: 'ellipsis',
  //         transition: 'box-shadow 0.2s, background 0.2s',
  //       }}
  //       title={
  //         isOcupado
  //           ? `Ocupado: ${arg.event.title}`
  //           : `Disponible: ${arg.event.title}`
  //       }
  //       onMouseOver={e => {
  //         e.currentTarget.style.boxShadow = isOcupado
  //           ? '0 4px 16px #e53e3e44'
  //           : '0 4px 16px #43c59e44';
  //         e.currentTarget.style.background = isOcupado
  //           ? '#c53030'
  //           : '#2f9e7e';
  //       }}
  //       onMouseOut={e => {
  //         e.currentTarget.style.boxShadow = isOcupado
  //           ? '0 2px 8px #e53e3e22'
  //           : '0 2px 8px #43c59e22';
  //         e.currentTarget.style.background = isOcupado
  //           ? '#e53e3e'
  //           : '#43c59e';
  //       }}
  //     >
  //       {isOcupado ? 'Ocupado' : arg.event.title}
  //     </div>
  //   );
  // }

  return (
    <section className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1f94c2ff] mb-2">Horarios y Citas Agendadas</h2>
          <p className="text-gray-600">
            Visualice la disponibilidad del doctor y las citas programadas.
          </p>
        </div>
        {/* Card doctor a la derecha del título */}
        {doctor && (
          <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg shadow px-4 py-2 max-w-md w-full md:w-auto">
            <img
              src={droxylog}
              alt="Dr. Droxi"
              className="w-14 h-14 rounded-full object-cover border-2 border-[#1f94c2ff]"
            />
            <div>
              <h4 className="font-semibold text-[#1f94c2ff] text-lg">{doctor.fullName || `${doctor.firstName || ''} ${doctor.firstLastName || ''}`}</h4>
              <p className="text-gray-600 text-sm">{doctor.specialty || doctor.service || 'Especialidad'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs arriba, horizontal */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('disponibilidad')}
            className={`${
              activeTab === 'disponibilidad'
                ? 'border-[#1f94c2ff] text-[#1f94c2ff] border-b-2'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 font-medium text-sm transition-all`}
          >
            Disponibilidad y Citas
          </button>
          <button
            onClick={() => setActiveTab('agendar')}
            className={`${
              activeTab === 'agendar'
                ? 'border-[#1f94c2ff] text-[#1f94c2ff] border-b-2'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 font-medium text-sm transition-all`}
          >
            Agendar cita
          </button>
        </nav>
      </div>

      {/* Contenido de la tab activa */}
      {loading ? (
        <div className="text-center py-12">
          <Cargando />
        </div>
      ) : (
        <>
          {activeTab === 'disponibilidad' ? (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Tarjetas de citas a la izquierda (1/4) */}
              <div className="lg:w-1/4 w-full space-y-4">
                {/* Tabs internas para agendadas/finalizadas */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setInternalTab('agendadas')}
                      className={`px-3 py-1 rounded font-medium text-sm transition-all ${internalTab === 'agendadas' ? 'bg-[#1f94c2ff] text-white' : 'bg-gray-100 text-[#1f94c2ff]'}`}
                    >
                      Agendadas
                    </button>
                    <button
                      onClick={() => setInternalTab('finalizadas')}
                      className={`px-3 py-1 rounded font-medium text-sm transition-all ${internalTab === 'finalizadas' ? 'bg-[#1f94c2ff] text-white' : 'bg-gray-100 text-[#1f94c2ff]'}`}
                    >
                      Finalizadas
                    </button>
                  </div>
                  <button
                    onClick={async () => {
                      console.log('🔄 Actualizando calendario manualmente...');
                      await actualizarCalendarioCompleto();
                      Swal.fire('¡Actualizado!', 'El calendario se ha actualizado con las últimas citas.', 'success');
                    }}
                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                    title="Actualizar calendario"
                  >
                    🔄
                  </button>
                </div>
                <div className="max-h-[700px] overflow-y-auto pr-2">
                  <div className="grid grid-cols-1 gap-4">
                    {(internalTab === 'agendadas'
                      ? calendarioData.citas.filter(c => (c.estado || '').toLowerCase() === 'agendada')
                      : calendarioData.citas.filter(c => (c.estado || '').toLowerCase() === 'finalizada' || (c.estado || '').toLowerCase() === 'finalizadas')
                    ).map((cita) => (
                      <div
                        key={cita._id}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => setModalCita(cita)}
                      >
                        <div className="flex items-start gap-3">
                          {cita.patient?.urlPictureProfile ? (
                          <img
                              src={cita.patient?.urlPictureProfile}
                            alt={cita.patient?.firstName || 'Usuario'}
                            className="w-12 h-12 rounded-full object-cover"
                            referrerPolicy="no-referrer"
                              onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div 
                            className={`w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 ${cita.patient?.urlPictureProfile ? 'hidden' : ''}`}
                            style={{ display: cita.patient?.urlPictureProfile ? 'none' : 'flex' }}
                          >
                            <svg className="w-7 h-7 text-[#1f94c2ff]" viewBox="0 0 30 30" fill="currentColor">
                              <path d="M14.06,0h1.88c.19.02.38.05.58.07,2.97.32,5.63,1.4,7.93,3.29,2.88,2.36,4.67,5.38,5.33,9.05.1.55.15,1.1.23,1.65v1.88c-.02.08-.04.15-.05.23-.33,4-1.99,7.37-4.98,10.02-3.31,2.94-7.24,4.19-11.66,3.71-3.34-.36-6.26-1.71-8.66-4.06C1.03,22.32-.48,18.01.13,13.01c.37-3.03,1.62-5.69,3.66-7.96C6.11,2.46,8.98.84,12.41.23c.55-.1,1.1-.15,1.65-.23ZM24.24,25.37c3.9-3.37,5.83-9.2,3.93-14.82-1.91-5.66-7.3-9.5-13.23-9.45-6.1.06-11.42,3.97-13.23,9.77-1.74,5.58.26,11.23,4,14.47.23-.13.47-.27.71-.4,1.29-.71,2.58-1.42,3.88-2.11.52-.28.78-.68.77-1.27,0-.4.01-.8,0-1.2,0-.14-.05-.31-.13-.43-.66-.96-1.18-2-1.5-3.12-.03-.11-.11-.23-.19-.31-.36-.39-.57-.83-.58-1.37,0-.67-.04-1.35.01-2.02.03-.36.22-.71.33-1.07.05-.14.13-.29.13-.43,0-.86,0-1.72,0-2.58.02-1.88.85-3.29,2.59-4.02,2.15-.9,4.37-.88,6.53-.02,1.7.68,2.64,2.23,2.62,4.07,0,.86-.01,1.72,0,2.58,0,.13.05.27.12.37.21.34.35.69.35,1.1,0,.67,0,1.35,0,2.02-.01.72-.35,1.26-.94,1.66-.11.07-.2.2-.26.32-.35.69-.59,1.44-1.04,2.05-.54.73-.69,1.46-.66,2.33.03.7.28,1.15.91,1.45,1.63.79,3.25,1.62,4.9,2.44Z"/>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {cita.patient?.firstName} {cita.patient?.firstLastName}
                            </h4>
                            <p className="text-sm text-gray-500">{formatDate(cita.date)}</p>
                            <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${
                              cita.estado === 'Agendada' ? 'bg-blue-100 text-blue-800' :
                              cita.estado === 'Completada' || cita.estado === 'Finalizada' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {cita.estado}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Calendario a la derecha (3/4) */}
              <div className="lg:w-3/4 w-full">
                <div className="bg-white border border-gray-200 rounded-lg shadow p-1 md:p-3">
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    events={calendarioData.citas.map(cita => ({
                      title: `${cita.patient?.firstName || 'Usuario'} ${cita.patient?.firstLastName || ''}`,
                      start: cita.start,
                      end: cita.end,
                      backgroundColor: '#1f94c2',
                      borderColor: '#1f94c2',
                      textColor: '#fff',
                    }))}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek,timeGridDay',
                    }}
                    locale={esLocale}
                    height={900}
                    contentHeight={900}
                    aspectRatio={1.6}
                  />
                </div>
              </div>
            </div>
          ) : (
            // Tab Agendar Cita
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#1f94c2ff] mb-2">Agendar Nueva Cita</h3>
                <p className="text-gray-600">
                  Selecciona la especialidad y el doctor, luego elija un horario disponible en el calendario.
                </p>
              </div>


              {/* Selectores dependientes */}
              <div className="mb-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Selector de Especialidad */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Especialidad <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={especialidadSeleccionada}
                      onChange={handleEspecialidadChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] focus:border-[#1f94c2ff]"
                      required
                    >
                      <option value="">Elige una especialidad</option>
                      {especialidades.map((esp) => (
                        <option key={esp.value} value={esp.value}>
                          {esp.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selector de Doctor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doctor <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={doctorSeleccionado}
                      onChange={handleDoctorChange}
                      disabled={!especialidadSeleccionada || cargandoDoctores}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] focus:border-[#1f94c2ff] disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">
                        {cargandoDoctores 
                          ? 'Cargando doctores...' 
                          : !especialidadSeleccionada 
                            ? 'Primero selecciona una especialidad' 
                            : 'Selecciona un doctor'
                        }
                      </option>
                      {doctores.map((doc) => (
                        <option key={doc._id} value={doc._id}>
                          {doc.fullName || `${doc.firstName || ''} ${doc.firstLastName || ''}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Mensaje informativo */}
                {especialidadSeleccionada && doctores.length === 0 && !cargandoDoctores && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-yellow-800 text-sm">
                      No hay doctores disponibles para la especialidad seleccionada.
                    </p>
                  </div>
                )}
              </div>
              {doctor && doctorSeleccionado ? (
                <>
                  {/* Modal del calendario */}
                  {showCalendarModal && doctor && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full relative animate-fadeIn max-h-[95vh] overflow-y-auto">
                        <button className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-[#1f94c2ff] font-bold z-10" onClick={() => setShowCalendarModal(false)}>×</button>
                        <DoctorCalendar doctor={doctor} show={true} />
                      </div>
                    </div>
                  )}
                </>
              ) : !doctorSeleccionado ? (
                <div className="text-center py-12">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="flex justify-center mb-4">
                      <svg className="w-12 h-12 text-[#1f94c2ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-[#1f94c2ff] mb-2">Selecciona un Doctor</h3>
                    <p className="text-gray-600">
                      Por favor, selecciona una especialidad y un doctor para ver la disponibilidad de horarios.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No se pudo cargar la información del doctor.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal Detalles de Cita */}
      {modalCita && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full relative animate-fadeIn max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 rounded-t-lg">
              <div className="flex justify-between items-center p-6">
                <h3 className="text-2xl font-bold text-[#1f94c2ff]">
                  Detalles de la Cita
                </h3>
                <button
                  className="text-gray-400 hover:text-[#1f94c2ff] text-2xl font-bold"
                  onClick={() => setModalCita(null)}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Sección de Estado */}
              <div className="flex items-center justify-between">
                <span className={`px-4 py-2 rounded-full border ${getStatusBadgeClass(modalCita.estado || modalCita.status)} font-medium text-sm`}>
                  {modalCita.estado || modalCita.status || 'Pendiente'}
                </span>
                <span className="text-sm text-gray-500">
                  Creada el {formatDate(modalCita.createdAt)}
                </span>
              </div>

              {/* Sección de Paciente */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {modalCita.patient?.urlPictureProfile ? (
                    <img
                        src={modalCita.patient?.urlPictureProfile}
                      alt={`Foto de ${modalCita.patient?.firstName || 'paciente'}`}
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#1f94c2ff]"
                      referrerPolicy="no-referrer"
                        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div 
                      className={`w-16 h-16 rounded-full border-2 border-[#1f94c2ff] flex items-center justify-center bg-gray-100 ${modalCita.patient?.urlPictureProfile ? 'hidden' : ''}`}
                      style={{ display: modalCita.patient?.urlPictureProfile ? 'none' : 'flex' }}
                    >
                      <svg className="w-10 h-10 text-[#1f94c2ff]" viewBox="0 0 30 30" fill="currentColor">
                        <path d="M14.06,0h1.88c.19.02.38.05.58.07,2.97.32,5.63,1.4,7.93,3.29,2.88,2.36,4.67,5.38,5.33,9.05.1.55.15,1.1.23,1.65v1.88c-.02.08-.04.15-.05.23-.33,4-1.99,7.37-4.98,10.02-3.31,2.94-7.24,4.19-11.66,3.71-3.34-.36-6.26-1.71-8.66-4.06C1.03,22.32-.48,18.01.13,13.01c.37-3.03,1.62-5.69,3.66-7.96C6.11,2.46,8.98.84,12.41.23c.55-.1,1.1-.15,1.65-.23ZM24.24,25.37c3.9-3.37,5.83-9.2,3.93-14.82-1.91-5.66-7.3-9.5-13.23-9.45-6.1.06-11.42,3.97-13.23,9.77-1.74,5.58.26,11.23,4,14.47.23-.13.47-.27.71-.4,1.29-.71,2.58-1.42,3.88-2.11.52-.28.78-.68.77-1.27,0-.4.01-.8,0-1.2,0-.14-.05-.31-.13-.43-.66-.96-1.18-2-1.5-3.12-.03-.11-.11-.23-.19-.31-.36-.39-.57-.83-.58-1.37,0-.67-.04-1.35.01-2.02.03-.36.22-.71.33-1.07.05-.14.13-.29.13-.43,0-.86,0-1.72,0-2.58.02-1.88.85-3.29,2.59-4.02,2.15-.9,4.37-.88,6.53-.02,1.7.68,2.64,2.23,2.62,4.07,0,.86-.01,1.72,0,2.58,0,.13.05.27.12.37.21.34.35.69.35,1.1,0,.67,0,1.35,0,2.02-.01.72-.35,1.26-.94,1.66-.11.07-.2.2-.26.32-.35.69-.59,1.44-1.04,2.05-.54.73-.69,1.46-.66,2.33.03.7.28,1.15.91,1.45,1.63.79,3.25,1.62,4.9,2.44Z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-semibold text-lg text-[#1f94c2ff] flex items-center gap-2">
                      <SvgIcon Icon={UserIcon} className="w-5 h-5" />
                      Información del Paciente
                    </h4>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <span className="text-gray-500">Nombre:</span>
                        <p className="font-medium">{modalCita.patient?.firstName} {modalCita.patient?.firstLastName}</p>
                      </div>
                      {modalCita.patient?.identificacion && (
                        <div>
                          <span className="text-gray-500">Identificación:</span>
                          <p className="font-medium">{modalCita.patient?.identificacion}</p>
                        </div>
                      )}
                      {modalCita.patient?.email && (
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <p className="font-medium">{modalCita.patient?.email}</p>
                        </div>
                      )}
                      {modalCita.patient?.telefono && (
                        <div>
                          <span className="text-gray-500">Teléfono:</span>
                          <p className="font-medium">{modalCita.patient?.telefono}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de Detalles de la Cita */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg text-[#1f94c2ff] flex items-center gap-2">
                  <SvgIcon Icon={Calendario} className="w-5 h-5" />
                  Detalles de la Cita
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <span className="text-gray-500">Fecha y Hora:</span>
                    <p className="font-medium text-blue-900">{formatDate(modalCita.date)}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <span className="text-gray-500">Servicio:</span>
                    <p className="font-medium text-blue-900">{modalCita.service || 'No especificado'}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <span className="text-gray-500">Tipo de Pagador:</span>
                    <p className="font-medium text-blue-900">{modalCita.payerType || 'No especificado'}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <span className="text-gray-500">Convenio:</span>
                    <p className="font-medium text-blue-900">{modalCita.covenantType || 'No especificado'}</p>
                  </div>
                </div>
              </div>

              {/* Sección de Documentos */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg text-[#1f94c2ff] flex items-center gap-2">
                  <SvgIcon Icon={DocumentIcon} className="w-5 h-5" />
                  Documentos de la Cita
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span>Consentimiento Informado</span>
                    <span className={`px-3 py-1 rounded-full text-sm ${modalCita.firmoConsentimiento ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {modalCita.firmoConsentimiento ? 'Firmado' : 'Pendiente'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sección de Notas */}
              {modalCita.notes && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-[#1f94c2ff] flex items-center gap-2">
                    <SvgIcon Icon={NotasIcon} className="w-5 h-5" />
                    Notas
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{modalCita.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DroxyAgendar;